import { useState, type FormEvent } from 'react'
import {
  AleloButton,
  AleloInput,
  AleloLayout,
  AleloSelect,
} from '@/components/AleloLayout'
import styles from '@/components/AleloLayout.module.css'
import { RangoAlert } from '@/components/rango'
import { executeSafely } from '@/utils/helpers'
import { useOrganizationPickerManager } from './hooks/useOrganizationPickerManager'
import locales from './locales/pt-BR.json'

/**
 * S13 — Organization picker.
 * Quando a transação traz organizações (`VITE`/contexto), o usuário escolhe
 * em uma lista; caso contrário, informa o nome. "Continuar sem organização"
 * sempre disponível.
 */
export default function OrganizationPickerScreen() {
  const {
    handleSelect,
    handleSkip,
    error: sdkError,
    organizations,
  } = useOrganizationPickerManager()
  const [organization, setOrganization] = useState('')
  const [error, setError] = useState<string>()
  const [fieldError, setFieldError] = useState<string>()
  const [pending, setPending] = useState<'select' | 'skip' | null>(null)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (pending) return
    setError(undefined)
    if (!organization.trim()) {
      setFieldError(locales.orgRequired)
      return
    }
    setFieldError(undefined)
    setPending('select')
    try {
      await executeSafely(() => handleSelect(organization.trim()))
    } catch {
      setError(locales.selectFailed)
    } finally {
      setPending(null)
    }
  }

  async function onSkip() {
    if (pending) return
    setError(undefined)
    setPending('skip')
    try {
      await executeSafely(() => handleSkip())
    } catch {
      setError(locales.skipFailed)
    } finally {
      setPending(null)
    }
  }

  const busy = pending !== null
  const hasList = organizations.length > 0

  return (
    <AleloLayout>
      <h1 className={styles.title}>{locales.title}</h1>
      <p className={styles.subtitle}>{locales.subtitle}</p>
      <form className={styles.stack} onSubmit={onSubmit} noValidate>
        {hasList ? (
          <AleloSelect
            id="organization"
            name="organization"
            label={locales.orgLabel}
            value={organization}
            error={fieldError}
            onChange={(e) => {
              setOrganization(e.target.value)
              setFieldError(undefined)
            }}
          >
            <option value="">{locales.orgPlaceholderSelected}</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.displayName || org.name}
              </option>
            ))}
          </AleloSelect>
        ) : (
          <AleloInput
            id="organization"
            name="organization"
            label={locales.orgLabel}
            supportText={locales.orgSupport}
            autoComplete="organization"
            placeholder={locales.orgPlaceholder}
            value={organization}
            error={fieldError}
            onChange={(e) => {
              setOrganization(e.target.value)
              setFieldError(undefined)
            }}
          />
        )}

        {error || sdkError ? (
          <RangoAlert tone="negative">{error || sdkError}</RangoAlert>
        ) : null}

        <div className={styles.actions}>
          <AleloButton
            type="submit"
            disabled={busy}
            loading={pending === 'select'}
          >
            {locales.continue}
          </AleloButton>
          <AleloButton
            type="button"
            variant="secondary"
            disabled={busy}
            loading={pending === 'skip'}
            onClick={() => void onSkip()}
          >
            {locales.skip}
          </AleloButton>
        </div>
      </form>
    </AleloLayout>
  )
}
